import { useState } from "react";
import { BsCopy, BsFolderPlus, BsGridFill, BsScissors } from "react-icons/bs";
import { FiRefreshCw } from "react-icons/fi";
import {
  MdClear,
  MdOutlineDelete,
  MdOutlineFileDownload,
  MdOutlineFileUpload,
} from "react-icons/md";
import { BiRename } from "react-icons/bi";
import PropTypes from "prop-types";
import { FaListUl, FaRegPaste } from "react-icons/fa6";
import LayoutToggler from "./LayoutToggler";
import { useFileNavigation } from "../../contexts/FileNavigationContext";
import { useSelection } from "../../contexts/SelectionContext";
import { useClipBoard } from "../../contexts/ClipboardContext";
import { useLayout } from "../../contexts/LayoutContext";
import { validateApiCallback } from "../../utils/validateApiCallback";
import { useTranslation } from "../../contexts/TranslationProvider";
import "./Toolbar.scss";

const Toolbar = ({
  allowCreateFolder = true,
  allowUploadFile = true,
  allowCutCopy = true,
  allowDelete = true, // НОВЫЙ ПРОПС
  onLayoutChange,
  onRefresh,
  onCreateFolder,
  triggerAction,
}) => {
  const [showToggleViewMenu, setShowToggleViewMenu] = useState(false);
  const { currentPath, currentFolder } = useFileNavigation();
  const { selectedFiles, setSelectedFiles, handleDownload } = useSelection();
  const { clipBoard, setClipBoard, handleCutCopy, handlePasting } = useClipBoard();
  const { activeLayout } = useLayout();
  const t = useTranslation();

  // Обработчик создания папки
  const handleCreateNewFolder = () => {
    if (onCreateFolder) {
      onCreateFolder(currentPath || "/");
    } else {
      // Старый способ если нет переданной функции
      triggerAction.show("createFolder");
    }
  };

  // Toolbar Items
  const toolbarLeftItems = [
    {
      icon: <BsFolderPlus size={17} strokeWidth={0.3} />,
      text: t("newFolder"),
      permission: allowCreateFolder && !!onCreateFolder, // Показываем только если есть функция создания
      onClick: handleCreateNewFolder,
    },
    {
      icon: <MdOutlineFileUpload size={18} />,
      text: t("upload"),
      permission: allowUploadFile,
      onClick: () => triggerAction.show("uploadFile"),
    },
    {
      icon: <FaRegPaste size={18} />,
      text: t("paste"),
      permission: !!clipBoard,
      onClick: handleFilePasting,
    },
  ];

  const toolbarRightItems = [
    {
      icon: activeLayout === "grid" ? <BsGridFill size={16} /> : <FaListUl size={16} />,
      title: t("changeView"),
      onClick: () => setShowToggleViewMenu((prev) => !prev),
    },
    {
      icon: <FiRefreshCw size={16} />,
      title: t("refresh"),
      onClick: () => {
        validateApiCallback(onRefresh, "onRefresh");
        setClipBoard(null);
      },
    },
  ];

  function handleFilePasting() {
    handlePasting(currentFolder);
  }

  const handleDownloadItems = () => {
    handleDownload();
    setSelectedFiles([]);
  };

  // Selected File/Folder Actions
  if (selectedFiles.length > 0) {
    return (
      <div className="toolbar file-selected">
        <div className="file-action-container">
          <div>
            <button className="item-action file-action" onClick={() => handleCutCopy(true)} hidden={!allowCutCopy}>
              <BsScissors size={18} />
              <span>{t("cut")}</span>
            </button>
            <button className="item-action file-action" onClick={() => handleCutCopy(false)} hidden={!allowCutCopy}>
              <BsCopy strokeWidth={0.1} size={17} />
              <span>{t("copy")}</span>
            </button>
            {clipBoard?.files?.length > 0 && (
              <button
                className="item-action file-action"
                onClick={handleFilePasting}
                hidden={!allowCutCopy}
              >
                <FaRegPaste size={18} />
                <span>{t("paste")}</span>
              </button>
            )}
            {selectedFiles.length === 1 && (
              <button
                className="item-action file-action"
                onClick={() => triggerAction.show("rename")}
                hidden={!allowCutCopy}
              >
                <BiRename size={19} />
                <span>{t("rename")}</span>
              </button>
            )}
            {selectedFiles.every(file => !file.isDirectory) && (
              <button className="item-action file-action" onClick={handleDownloadItems}>
                <MdOutlineFileDownload size={19} />
                <span>{t("download")}</span>
              </button>
            )}
            {allowDelete && ( // ПОКАЗЫВАЕМ ТОЛЬКО ЕСЛИ РАЗРЕШЕНО
              <button
                className="item-action file-action"
                onClick={() => triggerAction.show("delete")}
              >
                <MdOutlineDelete size={19} />
                <span>{t("delete") || "Скрыть"}</span>
              </button>
            )}
          </div>
          <button
            className="item-action file-action"
            title={t("clearSelection")}
            onClick={() => setSelectedFiles([])}
          >
            <span>
              {selectedFiles.length}{" "}
              {t(selectedFiles.length > 1 ? "itemsSelected" : "itemSelected")}
            </span>
            <MdClear size={18} />
          </button>
        </div>
      </div>
    );
  }
  //

  return (
    <div className="toolbar">
      <div className="fm-toolbar">
        <div>
          {toolbarLeftItems
            .filter((item) => item.permission)
            .map((item, index) => (
              <button 
                className="item-action" 
                key={index} 
                onClick={item.onClick}
                title={item.text}
              >
                {item.icon}
                <span>{item.text}</span>
              </button>
            ))}
        </div>
        <div>
          {toolbarRightItems.map((item, index) => (
            <div key={index} className="toolbar-left-items">
              <button className="item-action icon-only" title={item.title} onClick={item.onClick}>
                {item.icon}
              </button>
              {index !== toolbarRightItems.length - 1 && <div className="item-separator"></div>}
            </div>
          ))}

          {showToggleViewMenu && (
            <LayoutToggler
              setShowToggleViewMenu={setShowToggleViewMenu}
              onLayoutChange={onLayoutChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

Toolbar.displayName = "Toolbar";

Toolbar.propTypes = {
  allowCreateFolder: PropTypes.bool,
  allowUploadFile: PropTypes.bool,
  allowCutCopy: PropTypes.bool,
  allowDelete: PropTypes.bool, // ДОБАВЛЕН ПРОПС
  onLayoutChange: PropTypes.func,
  onRefresh: PropTypes.func,
  onCreateFolder: PropTypes.func,
  triggerAction: PropTypes.object.isRequired,
};

export default Toolbar;